import React, { useState, useRef, useEffect } from "react";
import { useSelector, useStore } from "react-redux";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import SelectionDropdown from "../SelectionDropdown";
import { useNavigate } from "react-router-dom";
import "./CHMPage.css";
import ProgressBar from "../Loader/ProgressBar";
import Spinner from "../Loader/Spinner";
import { prettyName, canonicalName, extractProp } from "../../utils/name";
import { GEO_SERVER_BASE_URL } from "../../config/constants";

/* ---------- safe fitBounds wrapper ---------- */
const safeFitBounds = (map, layer) => {
  if (!map || !layer) return;
  try {
    const bounds = layer.getBounds();
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    } else {
      console.warn("[CHMPage] safeFitBounds: invalid bounds, skipping.");
    }
  } catch (err) {
    console.warn("[CHMPage] safeFitBounds error:", err);
  }
};

const CropHealthMonitoringView = () => {
  const mapRef = useRef(null);
  let currentPolygonLayer = null;
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const hasShownKhasraTileErrorRef = useRef(false);


  const mapInstanceRef = useRef(null);
  const khasraHighlightRef = useRef(null);
  const villageBoundsRef = useRef(null);
  const chmLayerRef = useRef(null);
  const layerControlRef = useRef(null);
  const [isSatelliteVisible, setIsSatelliteVisible] = useState(true);
  const [areLayersHidden, setAreLayersHidden] = useState(false);
  const satelliteLayerRef = useRef(null);
  const geoJsonLayerRef = useRef(null);
  const legendRef = useRef(null);
  const [geoJsonLayer, setGeoJsonLayer] = useState(null);
  const imageLayerRef = useRef(null);
  const znidexLayerRef = useRef(null);
  const [showKhasra, setShowKhasra] = useState(false);
  const khasraLayerRef = useRef(null);
  const [stateLayer, setStateLayer] = useState(null);
  const [districtLayer, setDistrictLayer] = useState(null);
  const [tehsilLayer, setTehsilLayer] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedTehsil, setSelectedTehsil] = useState(null);
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [showLULC, setShowLULC] = useState(false);
  const [selectedTehsilForLULC, setSelectedTehsilForLULC] = useState(null);
  const lulcLayerRef = useRef(null);
  const [selectedSeason, setSelectedSeason] = useState("Rabi");

  const [currentKhasraLayer, setCurrentKhasraLayer] = useState(null);
  const navigate = useNavigate();
  const [selectedRegion, setSelectedRegion] = useState(null);

  const [images, setImages] = useState([]);
  const [dates, setDates] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(-1);
  const hasShownKhasraErrorRef = useRef(false);


  // New state variables for slider functionality
  const [isPlaying, setIsPlaying] = useState(false);
  const playIntervalRef = useRef(null);
  const LOADING_STATES = {
    IDLE: 'idle',
    KHASRA_LOADING: 'khasra_loading'
  };


  const {
    state: reduxState,
    district: reduxDistrict,
    tehsil: reduxTehsil,
    village: reduxVillage
  } = useSelector((state) => state.location);
  const { geojson } = useSelector((state) => state.geo);
  const store = useStore(); // Direct store access for Condition 2

  // 2. Add minimal state variables - Add these with your other useState declarations:
  const [loadingState, setLoadingState] = useState(LOADING_STATES.IDLE);
  const [loadingMessage, setLoadingMessage] = useState('');

  // 3. Add simplified getLoadingMessage function - Add this function:
  const getLoadingMessage = (state, progress) => {
    switch (state) {
      case LOADING_STATES.KHASRA_LOADING:
        if (progress < 50) return "Loading field boundaries...";
        return "Preparing parcel data...";
      default:
        return "Loading...";
    }
  };
  const geoServerBaseUrl =
    `${GEO_SERVER_BASE_URL}/ows?service=WFS&version=1.0.0&request=GetFeature`;
  const chmUrl = `${geoServerBaseUrl}&typeName=ne:Sardarshahar_CHM&outputFormat=application/json`;

  const baseLayers = {
    OpenStreetMap: L.tileLayer(
      "http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        attribution: "© OpenStreetMap contributors",
      }
    ),
    Satellite: L.tileLayer(
      "http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
      {
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
        attribution: "© Google Maps",
      }
    ),
    Hybrid: L.tileLayer("http://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}", {
      subdomains: ["mt0", "mt1", "mt2", "mt3"],
      attribution: "© Google Maps",
    }),
  };

  useEffect(() => {
    if (!mapInstanceRef.current) {
      const map = L.map(mapRef.current).setView([20, 78], 5);
      mapInstanceRef.current = map;

      const satelliteLayer = baseLayers["Satellite"].addTo(map);
      satelliteLayerRef.current = satelliteLayer;

      layerControlRef.current = L.control.layers(baseLayers, {}).addTo(map);
      chmLayerRef.current = L.layerGroup().addTo(map);
      geoJsonLayerRef.current = L.layerGroup().addTo(map);

      fetchCHMPoints();

      // ✅ Condition 2: After map loads, wait for cached thunks to resolve, then zoom
      setTimeout(() => {
        const { location, geo } = store.getState();
        if (location.village && geo.geojson.village && mapInstanceRef.current) {
          console.log("🔄 [Condition 2] Zooming to:", location.village);
          handleSearch(location.state, location.district, location.tehsil, location.village, geo.geojson.village);
        }
      }, 300);
    }
  }, []);

  // Removed conflicting useEffect that was overriding map bounds and layers.
  // The handleSearch function now manages all layer rendering and zooming logic.

  useEffect(() => {
    if (
      selectedState &&
      selectedDistrict &&
      selectedTehsil &&
      selectedVillage
    ) {
      fetchTrueColorImages(
        selectedState,
        selectedDistrict,
        selectedTehsil,
        selectedVillage,
        selectedSeason
      );
    }
  }, [selectedSeason]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
      }
    };
  }, []);

  const fetchTrueColorImages = async (
    state,
    district,
    tehsil,
    village,
    season
  ) => {
    if (!state || !district || !tehsil || !village || !season) return;
    const requestBody = { state, district, tehsil, village, season };

    console.log("📤 Sending TrueColor request body:", requestBody);

    setLoading(true);
    setProgress(10);

    const interval = setInterval(() => {
      setProgress((prev) => (prev < 95 ? prev + 5 : prev));
    }, 500);

    try {
      const response = await fetch("/api/get_true_color_images",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        }
      );

      const data = await response.json();
      if (data.images) {
        setImages(data.images);
        setDates(data.dates);
        setSelectedImage(data.images[0]?.image_url || null);
        setCurrentImageIndex(0);

        if (imageLayerRef.current) {
          mapInstanceRef.current.removeLayer(imageLayerRef.current);
          imageLayerRef.current.setZIndex(1);
        }

        if (tehsilLayer) {
          if (!villageBoundsRef.current) {
            console.warn(
              "⚠️ villageBoundsRef is NULL — using tehsil bounds instead!"
            );
          } else {
            console.log("✅ villageBoundsRef is set. Using village bounds.");
          }

          const bounds = villageBoundsRef.current || tehsilLayer.getBounds();

          if (mapInstanceRef.current) {
            const imageOverlay = L.imageOverlay(
              data.images[0]?.image_url,
              bounds
            ).addTo(mapInstanceRef.current);

            imageLayerRef.current = imageOverlay;
            imageOverlay.bringToBack();
          }
        }
      }

      setProgress(100);
    } catch (error) {
      console.error("Error fetching images:", error);
    } finally {
      clearInterval(interval);
      setTimeout(() => {
        setLoading(false);
        setProgress(0);
      }, 800);
    }
  };

  const handleSearch = (state, district, tehsil, village, geoJsonData) => {
    if (!mapInstanceRef.current) {
      console.warn("Map not initialized yet, skipping search handler");
      return;
    }

    setSelectedState(state);
    setSelectedDistrict(district);
    setSelectedTehsil(tehsil);
    setSelectedVillage(village);
    fetchCHMPoints(state);

    if (!geoJsonData || !geoJsonData.features) {
      console.warn("Invalid or missing GeoJSON data.");
      return;
    }

    // Use local variables to track layers within this function execution
    let currentDistrictLayer = districtLayer;
    let currentTehsilLayer = tehsilLayer;

    if (district) {
      // Always remove old district layer and recreate so fitBounds always fires
      if (currentDistrictLayer) {
        mapInstanceRef.current.removeLayer(currentDistrictLayer);
        currentDistrictLayer = null;
      }

      const getDistrictName = (feature) =>
        feature.properties.district ||
        feature.properties.District ||
        feature.properties.NAME_2;

      const newDistrictLayer = L.geoJSON(geoJsonData, {
        style: function (feature) {
          const featDistrict = getDistrictName(feature);
          const isSelected = canonicalName(featDistrict) === canonicalName(district);
          return {
            color: isSelected ? "blue" : "red",
            weight: isSelected ? 3 : 1,
            fillOpacity: 0,
          };
        },
        onEachFeature: function (feature, layer) {
          const districtName = feature.properties.District || feature.properties.district || feature.properties.NAME_2;
          if (tehsil) {
            /* If Tehsil selected, don't show District label */
            layer.closeTooltip();
            layer.unbindTooltip();
          } else {
            layer.bindTooltip(districtName, {
              permanent: true,
              direction: "center",
              className: "district-label",
            });
          }
        },
      }).addTo(mapInstanceRef.current);

      // Zoom to the selected district's bounds specifically
      const selectedDistrictLayer = newDistrictLayer.getLayers().find((l) => {
        const n = getDistrictName(l.feature);
        return canonicalName(n) === canonicalName(district);
      });

      if (selectedDistrictLayer) {
        mapInstanceRef.current.fitBounds(selectedDistrictLayer.getBounds(), { padding: [30, 30] });
      } else {
        mapInstanceRef.current.fitBounds(newDistrictLayer.getBounds());
      }

      setDistrictLayer(newDistrictLayer);
      currentDistrictLayer = newDistrictLayer;
    } else if (state && !district) {
      // ✅ State-only view: Render all districts with Labels and State highlight
      if (currentDistrictLayer) {
        mapInstanceRef.current.removeLayer(currentDistrictLayer);
      }
      if (tehsilLayer) {
        mapInstanceRef.current.removeLayer(tehsilLayer);
        setTehsilLayer(null);
      }
      if (geoJsonLayer) {
        mapInstanceRef.current.removeLayer(geoJsonLayer);
        setGeoJsonLayer(null);
      }

      const newStateLayer = L.geoJSON(geoJsonData, {
        style: {
          color: "red",
          weight: 2,
          fillOpacity: 0,
        },
        onEachFeature: function (feature, layer) {
          const districtName = feature.properties.District || feature.properties.district || feature.properties.NAME_2;
          layer.bindTooltip(districtName, {
            permanent: true,
            direction: "center",
            className: "district-label",
          });
          setTimeout(() => {
            const el = layer.getElement();
            if (el) el.style.pointerEvents = "none";
          }, 100);
        }
      }).addTo(mapInstanceRef.current);

      setDistrictLayer(newStateLayer);
      currentDistrictLayer = newStateLayer;
      mapInstanceRef.current.fitBounds(newStateLayer.getBounds());
    }

    if (tehsil) {
      setSelectedTehsil(tehsil);
      const getTehsilName = (feature) =>
        feature.properties?.tehsil ||
        feature.properties?.subdist ||
        feature.properties?.NAME_3;

      const normalizedTehsil = tehsil?.toLowerCase().trim();
      const normalizedDistrict = selectedDistrict?.toLowerCase().trim();

      // Filter features just for ZOOMING, but render ALL features
      const filteredTehsilFeatures = geoJsonData.features.filter((feature) => {
        const districtName =
          feature.properties?.District ||
          feature.properties?.district ||
          feature.properties?.NAME_2;
        const tehsilName = getTehsilName(feature);

        return (
          districtName?.toLowerCase().trim() === normalizedDistrict &&
          tehsilName?.toLowerCase().trim() === normalizedTehsil
        );
      });

      // Render ALL tehsils from the geoJsonData (which contains full district data)
      // ✅ Guard: Only render/zoom to Tehsil if NO village is selected (matches DEMView)
      if (geoJsonData.features && !village) {
        const allTehsilFeatures = geoJsonData.features || [];

        if (allTehsilFeatures.length > 0) {
          if (currentTehsilLayer) {
            mapInstanceRef.current.removeLayer(currentTehsilLayer);
          }

          const newTehsilLayer = L.geoJSON(
            { ...geoJsonData, features: allTehsilFeatures },
            {
              style: function (feature) {
                const tehsilName = getTehsilName(feature);
                const isSelected = tehsilName?.toLowerCase().trim() === normalizedTehsil;

                return {
                  color: isSelected ? "#DC2626" : "#FB923C", // Red for selected, Orange for others
                  weight: isSelected ? 3 : 1,
                  fillOpacity: isSelected ? 0.1 : 0,
                  fillColor: isSelected ? "#DC2626" : "transparent"
                };
              },
              onEachFeature: function (feature, layer) {
                const tehsilName =
                  feature.properties.NAME_3 || feature.properties.tehsil;

                const isSelected = tehsilName?.toLowerCase().trim() === normalizedTehsil;

                layer.bindPopup(`<b>Tehsil:</b> ${tehsilName}`);
                layer.bindTooltip(tehsilName, {
                  permanent: true, // Always show label
                  direction: "center",
                  className: isSelected ? "tehsil-label tehsil-label-selected" : "tehsil-label",
                });

                setTimeout(() => {
                  const el = layer.getElement();
                  if (el) el.style.pointerEvents = "none";
                }, 100);
              },
            }
          ).addTo(mapInstanceRef.current);

          setTehsilLayer(newTehsilLayer);
          currentTehsilLayer = newTehsilLayer; // Update local ref

          // Fit bounds to SELECTED tehsil if possible, otherwise all
          if (filteredTehsilFeatures.length > 0) {
            const tempLayer = L.geoJSON({
              type: "FeatureCollection",
              features: filteredTehsilFeatures,
            });
            mapInstanceRef.current.fitBounds(tempLayer.getBounds());
          } else {
            mapInstanceRef.current.fitBounds(newTehsilLayer.getBounds());
          }

        } else {
          if (currentTehsilLayer) {
            currentTehsilLayer.setStyle({ opacity: 1 });
          }
        }
      }
    }

    // ✅ Village layer handling matching DEMView.js
    if (village) {
      // Clean up previous village layer
      if (geoJsonLayer) {
        mapInstanceRef.current.removeLayer(geoJsonLayer);
      }

      const allFeatures = geoJsonData.features || [];

      if (allFeatures.length > 0) {
        // Import these helpers at the top of the file, but for now we can rely on them being available or define them here if not imported.
        // Assuming they are available or we need to inline them if imports are missing.
        // Ideally we should have added imports. I will assume imports are added in a separate step or I should add them now.
        // To be safe, I will use local helper if import is not guaranteed in this block, but the plan said to import.
        // Let's assume I will add imports in next step or previous.
        // Actually, I can't easily add imports in the same replace block if they are at the top.
        // I will use the logic assuming imports are present, and then add imports in another step.

        const newVillageLayer = L.geoJSON(
          { type: "FeatureCollection", features: allFeatures },
          {
            style: function (feature) {
              const rawName = extractProp(feature.properties, "village", "Village", "NAME_4", "NAME", "Name");
              const featVillage = canonicalName(rawName);
              const selectedVillageName = canonicalName(village);

              const isSelected = featVillage === selectedVillageName;
              return {
                color: isSelected ? "#7C3AED" : "#F97316", // Violet for selected, Orange for others
                weight: isSelected ? 3 : 1,
                fillOpacity: isSelected ? 0.15 : 0,
                fillColor: isSelected ? "#7C3AED" : "transparent",
              };
            },
            onEachFeature: function (feature, layer) {
              const rawName = extractProp(feature.properties, "village", "Village", "NAME_4", "NAME", "Name");
              if (!rawName) return;

              const displayName = prettyName(rawName);
              const featVillage = canonicalName(rawName);
              const selectedVillageName = canonicalName(village);
              const isSelected = featVillage === selectedVillageName;

              layer.bindPopup(`<b>Village:</b> ${displayName}`);
              layer.bindTooltip(displayName, {
                permanent: true,
                direction: "center",
                className: isSelected
                  ? "village-label village-label-selected"
                  : "village-label",
              });

              // Prevent clicking on the boundary interfering with other interactions if needed
              setTimeout(() => {
                const el = layer.getElement();
                if (el) el.style.pointerEvents = "none";
              }, 100);
            },
          }
        ).addTo(mapInstanceRef.current);

        setGeoJsonLayer(newVillageLayer);
        villageBoundsRef.current = newVillageLayer.getBounds(); // Update ref

        // Zoom to the SELECTED village specifically
        const selectedVillageName = canonicalName(village);

        const selectedFeatures = allFeatures.filter((f) => {
          const rawName = extractProp(f.properties, "village", "Village", "NAME_4", "NAME", "Name");
          const fv = canonicalName(rawName);
          return fv === selectedVillageName;
        });

        if (selectedFeatures.length > 0) {
          const tempLayer = L.geoJSON({
            type: "FeatureCollection",
            features: selectedFeatures,
          });

          if (mapInstanceRef.current) {
            safeFitBounds(mapInstanceRef.current, tempLayer);
          }
        } else {
          if (mapInstanceRef.current) {
            safeFitBounds(mapInstanceRef.current, newVillageLayer);
          }
        }

        // ✅ Hide tehsil and district when village is selected by REMOVING them
        // This ensures tooltips (labels) are gone.
        if (currentTehsilLayer && mapInstanceRef.current.hasLayer(currentTehsilLayer)) {
          mapInstanceRef.current.removeLayer(currentTehsilLayer);
        }
        if (currentDistrictLayer && mapInstanceRef.current.hasLayer(currentDistrictLayer)) {
          mapInstanceRef.current.removeLayer(currentDistrictLayer);
        }
      } else {
        // No features found
        if (geoJsonLayer) {
          mapInstanceRef.current.removeLayer(geoJsonLayer);
          setGeoJsonLayer(null);
        }
        // Re-show tehsil if village features missing?
        if (currentTehsilLayer && !mapInstanceRef.current.hasLayer(currentTehsilLayer)) {
          currentTehsilLayer.addTo(mapInstanceRef.current);
        }
      }
    } else {
      // No village selected
      if (geoJsonLayer) {
        mapInstanceRef.current.removeLayer(geoJsonLayer);
        setGeoJsonLayer(null);
      }

      if (currentTehsilLayer && !mapInstanceRef.current.hasLayer(currentTehsilLayer)) {
        currentTehsilLayer.addTo(mapInstanceRef.current);
      }
      if (currentTehsilLayer) {
        currentTehsilLayer.setStyle({ opacity: 1, fillOpacity: 0 });
        mapInstanceRef.current.fitBounds(currentTehsilLayer.getBounds());
      }

      if (currentDistrictLayer && !mapInstanceRef.current.hasLayer(currentDistrictLayer)) {
        currentDistrictLayer.addTo(mapInstanceRef.current);
      }
      if (currentDistrictLayer) currentDistrictLayer.setStyle({ opacity: 0.3 });
    }
  };

  const cropColorsByLayer = {
    "Field Image Geo tagging": {
      "Bengal Gram (Chana)": "#FFC300",
      "Wheat - IR": "#880808",
      default: "#808080",
    },
    "Assign lat long": {
      "Bengal Gram (Chana)": "#F88379",
      "Wheat - IR": "#CC5500",
      default: "#A9A9A9",
    },
  };

  const layerColors = {
    "Field Image Geo tagging": "#FF5733",
    "Assign lat long": "#3498DB",
  };

  const bringKhasraToFront = () => {
    if (khasraLayerRef.current) {
      khasraLayerRef.current.bringToFront();
    }
  };

  const fetchCHMPoints = async (state) => {
    if (!state) return;

    let chmUrls = [];

    if (state === "Rajasthan") {
      chmUrls = [
        {
          url: `${geoServerBaseUrl}&typeName=ne:Sardarshahar_CHM&outputFormat=application/json`,
          layerName: "Sardarshahar_CHM",
        },
      ];
    } else if (state === "Maharashtra") {
      chmUrls = [
        {
          url: `${geoServerBaseUrl}&typeName=ne:Field Image Geo tagging&outputFormat=application/json`,
          layerName: "Field Image Geo tagging",
        },
        {
          url: `${geoServerBaseUrl}&typeName=ne:Assign lat long&outputFormat=application/json`,
          layerName: "Assign lat long",
        },
      ];
    } else {
      return;
    }

    setLoading(true);
    setProgress(5);
    const interval = setInterval(() => {
      setProgress((p) => (p < 90 ? p + 3 : p));
    }, 150);

    try {
      for (const { url, layerName } of chmUrls) {
        const response = await fetch(url);
        const data = await response.json();

        if (!data.features || data.features.length === 0) {
          console.warn(`No features found for ${state} from ${layerName}`);
          continue;
        }

        addCHMPoints(data.features, state, layerName);
      }

      setProgress(100);
    } catch (error) {
      console.error("Error fetching CHM points:", error);
    } finally {
      clearInterval(interval);
      setTimeout(() => {
        setLoading(false);
        setProgress(0);
      }, 500);
    }
  };

  const handleHideLayers = () => {
    if (areLayersHidden) {
      if (districtLayer) districtLayer.setStyle({ opacity: 1 });
      if (tehsilLayer) tehsilLayer.setStyle({ opacity: 1 });
      if (geoJsonLayer) geoJsonLayer.setStyle({ opacity: 1 });
    } else {
      if (districtLayer) districtLayer.setStyle({ opacity: 0 });
      if (tehsilLayer) tehsilLayer.setStyle({ opacity: 0 });
      if (geoJsonLayer) geoJsonLayer.setStyle({ opacity: 0 });
    }

    setAreLayersHidden(!areLayersHidden);
  };

  const [enabledCrops, setEnabledCrops] = useState({
    "Green Gram (Moong)": true,
    "Groundnut (Pea Nut)": true,
    "Pearl Millet (Bajra)": true,
  });

  const setupKhasraClickHandler = (
    map,
    khasraLayer,
    villageName,
    selectedTehsil
  ) => {
    // ✅ Remove any existing click handlers first
    map.off("click");

    // ✅ Add new click handler
    map.on("click", async (e) => {
      // ✅ REMOVED the showKhasra check - this was causing the issue!
      // The handler should work if the khasraLayer exists
      if (!khasraLayer || !map) {
        console.log("❌ Khasra layer not available, ignoring click");
        return;
      }

      console.log("🖱️ Khasra click detected"); // Debug log

      const { lat, lng } = e.latlng;
      const layerName = khasraLayer.wmsParams.layers;

      // ✅ Use the same filter logic as in toggleKhasraLayer
      let cqlFilter = `state='${selectedState}' AND district='${selectedDistrict}' AND tehsil='${selectedTehsil}'`;

      // If village is selected and displayed (geoJsonLayer exists), filter for that village only
      if (villageName && geoJsonLayer) {
        cqlFilter += ` AND village='${villageName}'`;
      }

      console.log("🔍 Using CQL filter:", cqlFilter); // Debug log

      // ✅ Use correct point conversion
      const point = map.latLngToContainerPoint(e.latlng);
      const size = map.getSize();

      const wmsUrl =
        `${GEO_SERVER_BASE_URL}/wms?service=WMS&version=1.1.1&request=GetFeatureInfo` +
        `&layers=${layerName}&query_layers=${layerName}` +
        `&info_format=application/json` +
        `&x=${Math.floor(point.x)}&y=${Math.floor(point.y)}` +
        `&height=${size.y}&width=${size.x}` +
        `&srs=EPSG:4326&bbox=${map.getBounds().toBBoxString()}` +
        `&cql_filter=${encodeURIComponent(cqlFilter)}`;

      console.log("🌐 WMS URL:", wmsUrl); // Debug log

      try {
        const response = await fetch(wmsUrl);
        const data = await response.json();

        console.log("📊 WMS Response:", data); // Debug log

        if (!data.features || data.features.length === 0) {
          console.warn("❌ No feature returned from WMS click.");
          return;
        }

        const feature = data.features[0];
        const props = feature.properties;

        const tableFields = [
          { name: "clicked_lat", header: "Latitude" },
          { name: "clicked_lng", header: "Longitude" },
          { name: "state", header: "State" },
          { name: "district", header: "District" },
          { name: "tehsil", header: "Tehsil" },
          { name: "village", header: "Village" },
          { name: "khasra_no", header: "Khasra No" },
          { name: "area_ac", header: "Area (Ac)" },
          { name: "lu_lc", header: "LU/LC" },
        ];

        let tableHtml = `<table class="popup-table"><thead></thead><tbody>`;
        tableFields.forEach((f) => {
          let value =
            f.name === "clicked_lat"
              ? lat.toFixed(6)
              : f.name === "clicked_lng"
                ? lng.toFixed(6)
                : props[f.name] ?? "N/A";

          tableHtml += `<tr><td><strong>${f.header}</strong></td><td>${value}</td></tr>`;
        });

        // ✅ Add data source attribution to popup
        tableHtml += `<tr><td colspan="2" style="text-align: center; font-style: italic; color: #666; padding-top: 8px; border-top: 1px solid #ddd;">
          <small>Source: Land Revenue Department</small>
        </td></tr>`;

        tableHtml += `</tbody></table>`;

        // ✅ Remove previous highlight before adding new one
        if (khasraHighlightRef.current) {
          map.removeLayer(khasraHighlightRef.current);
          khasraHighlightRef.current = null;
        }

        const highlight = L.geoJSON(feature.geometry, {
          style: {
            color: "red",
            weight: 2,
            fillOpacity: 0.2,
          },
          onEachFeature: (f, layer) => {
            layer.bindPopup(tableHtml).openPopup();
          },
        }).addTo(map);

        khasraHighlightRef.current = highlight;
        map.fitBounds(highlight.getBounds());

        console.log("✅ Khasra feature highlighted");

      } catch (err) {
        console.error("Khasra click fetch failed:", err);
      }
    });

    console.log("✅ Khasra click handler setup complete");
  };

  const toggleKhasraLayer = async (
    selectedTehsil,
    selectedState,
    selectedDistrict,
    villageName
  ) => {
    try {
      setLoadingState(LOADING_STATES.KHASRA_LOADING);
      setProgress(20);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.KHASRA_LOADING, 20));

      if (!mapInstanceRef.current) {
        console.warn("Map not ready for Khasra toggle");
        setLoadingState(LOADING_STATES.IDLE);
        return;
      }

      if (!selectedTehsil || !selectedState || !selectedDistrict) {
        console.error("Missing input for Khasra layer filter.");
        setLoadingState(LOADING_STATES.IDLE);
        setProgress(0);
        setLoadingMessage('');
        return;
      }

      if (showKhasra && khasraLayerRef.current) {
        // ✅ CLEANUP when hiding Khasra

        // 1. Remove the Khasra layer
        mapInstanceRef.current.removeLayer(khasraLayerRef.current);
        khasraLayerRef.current = null;

        // 2. Remove any existing highlight
        if (khasraHighlightRef.current) {
          mapInstanceRef.current.removeLayer(khasraHighlightRef.current);
          khasraHighlightRef.current = null;
        }

        // 3. Remove the click handler - IMPORTANT!
        mapInstanceRef.current.off("click");

        // 4. Update states
        setShowKhasra(false);
        setLoadingState(LOADING_STATES.IDLE);
        setProgress(0);
        setLoadingMessage('');

        console.log("✅ Khasra layer hidden and click handler removed");
        return;
      }

      setProgress(40);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.KHASRA_LOADING, 40));

      let khasraLayerName = "";
      if (selectedState === "Rajasthan") {
        khasraLayerName = "ne:Rajasthan_Khasra_All1";
      } else if (selectedState === "Maharashtra") {
        khasraLayerName = "ne:MH_Khasra_All";
      } else if (selectedState === "Uttar Pradesh") {
        khasraLayerName = "ne:UP_Khasra_All";
      } else if (selectedState === "Andhra Pradesh") {
        khasraLayerName = "ne:AP_Khasra_All";
      } else if (selectedState === "Karnataka") {
        khasraLayerName = "ne:Karnataka_KHASRA_ALL";
      } else {
        alert("No Khasra layer configured for this state.");
        setLoadingState(LOADING_STATES.IDLE);
        setProgress(0);
        setLoadingMessage('');
        return;
      }

      setProgress(60);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.KHASRA_LOADING, 60));

      // ✅ Use prettyName for WMS compatibility
      const displayState = prettyName(selectedState);
      const displayDistrict = prettyName(selectedDistrict);
      const displayTehsil = prettyName(selectedTehsil);

      let cqlFilter = `state='${displayState}' AND district='${displayDistrict}' AND tehsil='${displayTehsil}'`;

      // If village is selected and displayed (geoJsonLayer exists), filter for that village only
      if (villageName && geoJsonLayer) {
        const displayVillage = prettyName(villageName);
        cqlFilter += ` AND village='${displayVillage}'`;
        console.log(`🎯 Village-level Khasra filter: ${cqlFilter}`);
      } else {
        // If no village selected, show all Khasra for the tehsil
        console.log(`🏘️ Tehsil-level Khasra filter: ${cqlFilter}`);
      }

      if (!mapInstanceRef.current.getPane("khasraPane")) {
        mapInstanceRef.current.createPane("khasraPane");
        mapInstanceRef.current.getPane("khasraPane").style.zIndex = 450;
      }

      const khasraLayer = L.tileLayer.wms(
        `${GEO_SERVER_BASE_URL}/wms`,
        {
          layers: khasraLayerName,
          format: "image/png",
          transparent: true,
          version: "1.1.0",
          attribution: "Land Revenue Department",
          pane: "khasraPane",
          zIndex: 1000,
          CQL_FILTER: cqlFilter,
        }
      );

      // Add loading event handlers for Khasra
      khasraLayer.on('loading', () => {
        setProgress(80);
        setLoadingMessage(getLoadingMessage(LOADING_STATES.KHASRA_LOADING, 80));
      });

      khasraLayer.on('load', () => {
        setProgress(100);
        setLoadingMessage(getLoadingMessage(LOADING_STATES.KHASRA_LOADING, 100));

        setTimeout(() => {
          setLoadingState(LOADING_STATES.IDLE);
          setProgress(0);
          setLoadingMessage('');
        }, 400);
      });

      khasraLayer.on('tileerror', (error) => {
        console.error('Khasra layer loading error:', error);
        setLoadingState(LOADING_STATES.IDLE);
        setProgress(0);
        setLoadingMessage('');

        if (!hasShownKhasraTileErrorRef.current) {
          alert('Failed to load Khasra layer. Please try again.');
          hasShownKhasraTileErrorRef.current = true;

          setTimeout(() => {
            hasShownKhasraTileErrorRef.current = false;
          }, 10000); // Reset after 10 seconds
        }
      });


      khasraLayer.addTo(mapInstanceRef.current);
      khasraLayerRef.current = khasraLayer;

      // ✅ Set showKhasra to true BEFORE setting up click handler
      setShowKhasra(true);

      // ✅ Setup click handler AFTER setting state
      setupKhasraClickHandler(
        mapInstanceRef.current,
        khasraLayer,
        villageName,
        selectedTehsil
      );

      console.log("✅ Khasra layer shown and click handler activated");
      setTimeout(() => ensureKhasraOnTop(), 100);

    } catch (err) {
      console.error("Failed to toggle Khasra:", err);
      setLoadingState(LOADING_STATES.IDLE);
      setProgress(0);
      setLoadingMessage('');
    }
  };

  const handleKhasraButtonClick = () => {
    // Get the village name from the currently displayed village layer
    let currentVillageName = selectedVillage ? selectedVillage : null;

    if (!currentVillageName && geoJsonLayer && geoJsonLayer.getLayers().length > 0) {
      // Extract village name from the displayed geoJsonLayer (fallback)
      const villageFeature = geoJsonLayer.getLayers()[0].feature;
      if (villageFeature && villageFeature.properties) {
        currentVillageName = villageFeature.properties.village ||
          villageFeature.properties.NAME_4 ||
          villageFeature.properties.NAME;
      }
    }

    console.log('🔍 Current village for Khasra:', currentVillageName);
    console.log('🗺️ geoJsonLayer exists:', !!geoJsonLayer);

    toggleKhasraLayer(
      selectedTehsil,
      selectedState,
      selectedDistrict,
      currentVillageName
    );
  };

  const ensureKhasraOnTop = () => {
    if (khasraLayerRef.current && showKhasra) {
      if (!mapInstanceRef.current.getPane("khasraPane")) {
        mapInstanceRef.current.createPane("khasraPane");
        mapInstanceRef.current.getPane("khasraPane").style.zIndex = 1000;
      }

      mapInstanceRef.current.getPane("khasraPane").style.zIndex = 1000;

      if (imageLayerRef.current) {
        imageLayerRef.current.setZIndex(100);
      }

      if (lulcLayerRef.current && showLULC) {
        lulcLayerRef.current.setZIndex(500);
      }
    }
  };

  const cropColors = {
    "Green Gram (Moong)": "#ff7800",
    "Groundnut (Pea Nut)": "#007bff",
    "Pearl Millet (Bajra)": "#28a745",
    "Bengal Gram (Chana)": "#A52A2A",
    "Wheat - IR": "#8B4513",
    default: "#808080",
  };

  useEffect(() => {
    if (selectedState) {
      fetchCHMPoints(selectedState);
    }
  }, [enabledCrops, selectedState]);

  const addCHMPoints = (features, state, layerName) => {
    if (!chmLayerRef.current) return;

    features.forEach((feature) => {
      const { geometry, properties } = feature;
      if (!geometry || !properties) return;

      let fillColor;
      let popupContent = `<b>Layer:</b> ${layerName}<br>`;

      if (state === "Rajasthan") {
        const cropType = properties["Crop Name"] || "default";
        if (!enabledCrops[cropType]) return;

        fillColor =
          cropColorsByLayer[layerName]?.[cropType] ||
          cropColorsByLayer[layerName]?.["default"] ||
          cropColors[cropType] ||
          cropColors["default"];

        popupContent += `
          <b>Season:</b> ${properties["Season"] || "N/A"}<br>
          <b>Year:</b> ${properties["Year"] || "N/A"}<br>
          <b>Current Stage:</b> ${properties["Current St"] || "N/A"}<br>
          <b>Survey Date:</b> ${properties["DateOfSurv"] || "N/A"}
        `;
      } else if (state === "Maharashtra") {
        const layerColors = {
          "Field Image Geo tagging": "#FF5733",
          "Assign lat long": "#3498DB",
        };
        fillColor = layerColors[layerName] || "#808080";

        if (layerName === "Field Image Geo tagging") {
          popupContent += `
            <b>Request ID:</b> ${properties["RequestID"] || "N/A"}<br>
            <b>District:</b> ${properties["District"] || "N/A"}<br>
            <b>Taluka:</b> ${properties["TALUKA"] || "N/A"}<br>
            <b>Crop:</b> ${properties["Crop Name"] || "N/A"}<br>
            <b>Village:</b> ${properties["Village"] || "N/A"}<br>
            <b>Land Survey No:</b> ${properties["Land Surve"] || "N/A"}<br>
            <b>Land SubDiv No:</b> ${properties["Land SubDi"] || "N/A"}<br>
            <b>Farmer Contact:</b> ${properties["Farmer Con"] || "N/A"}<br>
            <b>Lat:</b> ${properties["lat"] || "N/A"}<br>
            <b>Long:</b> ${properties["long"] || "N/A"}
          `;
        } else if (layerName === "Assign lat long") {
          popupContent += `
            <b>Request ID:</b> ${properties["RequestID"] || "N/A"}<br>
            <b>District:</b> ${properties["District"] || "N/A"}<br>
            <b>Taluka:</b> ${properties["TALUKA"] || "N/A"}<br>
            <b>Crop:</b> ${properties["Crop Name"] || "N/A"}<br>
            <b>Reference:</b> ${properties["Reference"] || "N/A"}<br>
            <b>Village:</b> ${properties["Village"] || "N/A"}<br>
            <b>Land Survey No:</b> ${properties["Land Surve"] || "N/A"}<br>
            <b>Land SubDiv No:</b> ${properties["Land SubDi"] || "N/A"}<br>
            <b>Farmer Contact:</b> ${properties["Farmer Con"] || "N/A"}<br>
            <b>Lat:</b> ${properties["lat"] || "N/A"}<br>
            <b>Long:</b> ${properties["long"] || "N/A"}
          `;
        }
      } else {
        return;
      }

      const lon = geometry.coordinates[0];
      const lat = geometry.coordinates[1];

      L.circleMarker([lat, lon], {
        radius: 6,
        fillColor: fillColor,
        color: "black",
        weight: 1,
        opacity: 1,
        fillOpacity: 0.8,
      })
        .bindPopup(popupContent)
        .addTo(chmLayerRef.current);
    });
  };

  useEffect(() => {
    if (showKhasra) {
      ensureKhasraOnTop();
    }
  }, [imageLayerRef.current, showLULC, tehsilLayer, districtLayer]);

  // Timeline functionality
  const handleTimelineClick = (index) => {
    const imageUrl = images[index]?.image_url;

    if (currentImageIndex === index && selectedImage === imageUrl) {
      // If clicking the same image, hide it
      setSelectedImage(null);
      setCurrentImageIndex(-1);

      if (imageLayerRef.current) {
        mapInstanceRef.current.removeLayer(imageLayerRef.current);
        imageLayerRef.current = null;
      }
    } else {
      // Show new image
      setSelectedImage(imageUrl);
      setCurrentImageIndex(index);

      if (imageLayerRef.current) {
        mapInstanceRef.current.removeLayer(imageLayerRef.current);
      }

      const bounds = villageBoundsRef.current || tehsilLayer?.getBounds();

      if (bounds) {
        const imageOverlay = L.imageOverlay(imageUrl, bounds).addTo(mapInstanceRef.current);
        imageLayerRef.current = imageOverlay;
        imageOverlay.setZIndex(100);
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Slider functionality
  const handleSliderChange = (e) => {
    const index = parseInt(e.target.value);
    handleTimelineClick(index);
  };

  const playTimeline = () => {
    if (isPlaying) {
      clearInterval(playIntervalRef.current);
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
      playIntervalRef.current = setInterval(() => {
        setCurrentImageIndex(prev => {
          const nextIndex = prev >= images.length - 1 ? 0 : prev + 1;
          handleTimelineClick(nextIndex);
          return nextIndex;
        });
      }, 2000); // Change image every 2 seconds
    }
  };

  const stopPlay = () => {
    clearInterval(playIntervalRef.current);
    setIsPlaying(false);
  };

  const getSliderDateLabels = () => {
    if (dates.length === 0) return null;

    return (
      <div className="slider-date-labels">
        {dates.map((date, index) => (
          <div
            key={index}
            className={`slider-date-label ${currentImageIndex === index ? 'active' : ''}`}
            style={{ left: `${(index / (dates.length - 1)) * 100}%` }}
          >
            {formatDate(date)}
          </div>
        ))}
      </div>
    );
  };

  const bringCHMPointsToFront = () => {
    if (chmLayerRef.current) {
      chmLayerRef.current.eachLayer((layer) => {
        if (layer.bringToFront) {
          layer.bringToFront();
        }
      });
    }
  };

  return (
    <div className="chm-page-container">
      <div className="header">
        <h3>Crop Health Monitoring</h3>
      </div>

      <div className="header-controls">

      </div>

      <div className="content-container">
        <div className="left-panel">
          <h3 className="label">Select Crops:</h3>
          {Object.keys(enabledCrops).map((crop) => (
            <label
              key={crop}
              className="crop-label"
              style={{
                backgroundColor: cropColors[crop] || cropColors["default"],
                color: "#fff",
              }}
            >
              <input
                type="checkbox"
                checked={enabledCrops[crop]}
                onChange={() => {
                  setEnabledCrops((prev) => ({ ...prev, [crop]: !prev[crop] }));
                }}
              />
              {crop}
            </label>
          ))}

          <h3 className="label">Select Season:</h3>
          <select
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(e.target.value)}
            className="season-select"
          >
            <option value="Rabi">Rabi</option>
            <option value="Kharif">Kharif</option>
          </select>
        </div>

        <div className="map-container">
          <div className="control-panel">
            <SelectionDropdown onSearch={handleSearch} />

            <div className="toggle-group" style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button
                onClick={handleKhasraButtonClick}
                className={`modern-btn ${showKhasra ? 'toggle-active' : 'toggle-inactive'}`}
                disabled={!selectedState || !selectedTehsil || loadingState === LOADING_STATES.KHASRA_LOADING}
                title={showKhasra ? "Hide Khasra Layer" : "Show Khasra Layer"}
              >
                {loadingState === LOADING_STATES.KHASRA_LOADING ? (
                  <>
                    <span className="spinner-sm"></span> Loading...
                  </>
                ) : (
                  <>
                    {showKhasra ? "👁️ Hide Khasra" : "🗺️ Show Khasra"}
                  </>
                )}
              </button>

              <button
                onClick={() => {
                  if (satelliteLayerRef.current) {
                    if (isSatelliteVisible) {
                      satelliteLayerRef.current.setOpacity(0);
                    } else {
                      satelliteLayerRef.current.setOpacity(1);
                    }
                    setIsSatelliteVisible(!isSatelliteVisible);
                  }
                }}
                className={`modern-btn ${isSatelliteVisible ? 'toggle-active' : 'toggle-inactive'}`}
                title={isSatelliteVisible ? "Switch to Regular Map" : "Switch to Satellite View"}
              >
                {isSatelliteVisible ? "🛰️ Hide Satellite" : "🌍 Show Satellite"}
              </button>
            </div>
          </div>

          <div ref={mapRef} className="map-ref" />

          {/* Timeline Slider Component */}
          {dates.length > 0 && (
            <div className={`timeline-container ${dates.length > 0 ? 'visible' : ''}`}>
              <div className="timeline-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div className="timeline-indicator"></div>
                  <h4 className="timeline-title">Satellite Images</h4>
                </div>
                <div className="timeline-date-display">
                  {currentImageIndex >= 0 ? formatDate(dates[currentImageIndex]) : 'Select Date'}
                </div>
              </div>
              <div className="slider-container">
                <div className="slider-track"></div>
                <input
                  type="range"
                  min="0"
                  max={dates.length - 1}
                  value={currentImageIndex >= 0 ? currentImageIndex : 0}
                  onChange={handleSliderChange}
                  className="slider-input"
                />
                <div className="slider-marks">
                  {dates.map((_, index) => (
                    <div
                      key={index}
                      className="slider-mark"
                      style={{ left: `${(index / (dates.length - 1)) * 90}%` }}
                    />
                  ))}
                </div>
                {getSliderDateLabels()}
                <div className="play-controls">
                  <button
                    className="play-btn"
                    onClick={playTimeline}
                    disabled={dates.length === 0}
                    title={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? "⏸" : "▶"}
                  </button>
                  <button
                    className="play-btn"
                    onClick={stopPlay}
                    disabled={!isPlaying}
                    title="Stop"
                  >
                    ⏹
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <>
          <Spinner />
          <ProgressBar loading={loading} progress={progress} />
        </>
      )}
    </div>
  );
};

export default CropHealthMonitoringView;
